import { forwardRef, useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  author?: string;
  additionalStructuredData?: Record<string, any>;
}

export const SEO = forwardRef<unknown, SEOProps>(function SEO({
  title,
  description,
  keywords = 'photography, portrait, landscape, wedding, boudoir, branding, dark moody photography',
  image = '/og-image.jpg',
  url,
  type = 'website',
  publishedTime,
  author = 'Raven Moon Photography',
  additionalStructuredData,
}: SEOProps, _ref) {
  const fullTitle = `${title} | Raven Moon Photography`;
  const siteUrl = window.location.origin;
  const fullUrl = url ? `${siteUrl}${url}` : window.location.href;
  const fullImage = image.startsWith('http') ? image : `${siteUrl}${image}`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Helper to set meta tag
    const setMeta = (name: string, content: string, property = false) => {
      const attr = property ? 'property' : 'name';
      let meta = document.querySelector(`meta[${attr}="${name}"]`);
      if (!meta) {
        meta = document.createElement('meta');
        meta.setAttribute(attr, name);
        document.head.appendChild(meta);
      }
      meta.setAttribute('content', content);
    };

    // Standard meta tags
    setMeta('description', description);
    setMeta('keywords', keywords);
    setMeta('author', author);

    // Open Graph
    setMeta('og:title', fullTitle, true);
    setMeta('og:description', description, true);
    setMeta('og:image', fullImage, true);
    setMeta('og:url', fullUrl, true);
    setMeta('og:type', type, true);
    setMeta('og:site_name', 'Raven Moon Photography', true);

    // Twitter Card
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:title', fullTitle);
    setMeta('twitter:description', description);
    setMeta('twitter:image', fullImage);

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', fullUrl);

    // Article specific
    if (type === 'article' && publishedTime) {
      setMeta('article:published_time', publishedTime, true);
      setMeta('article:author', author, true);
    }

    // JSON-LD Structured Data
    const existingScript = document.querySelector('script[data-seo="structured-data"]');
    if (existingScript) {
      existingScript.remove();
    }

    const structuredData = type === 'article' 
      ? {
          '@context': 'https://schema.org',
          '@type': 'Article',
          name: 'Raven Moon Photography',
          description,
          url: fullUrl,
          image: fullImage,
          headline: title,
          datePublished: publishedTime,
          author: {
            '@type': 'Person',
            name: author,
          },
        }
      : {
          '@context': 'https://schema.org',
          '@type': 'LocalBusiness',
          '@id': `${siteUrl}/#business`,
          name: 'Raven Moon Photography',
          description: 'Professional photography studio specializing in family portraits, brand photography, weddings, boudoir, and nature photography. Based in Portland, Oregon with worldwide travel availability.',
          url: siteUrl,
          image: fullImage,
          telephone: '+1-480-576-6053',
          email: 'ravenmoonphotography0@gmail.com',
          priceRange: '$$',
          currenciesAccepted: 'USD',
          paymentAccepted: 'Cash, Credit Card, Venmo, Zelle, PayPal',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Portland',
            addressRegion: 'OR',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 45.5152,
            longitude: -122.6784,
          },
          areaServed: [
            {
              '@type': 'City',
              name: 'Portland',
              '@id': 'https://www.wikidata.org/wiki/Q6106',
            },
            {
              '@type': 'State',
              name: 'Oregon',
            },
            {
              '@type': 'Country',
              name: 'United States',
            },
            {
              '@type': 'Text',
              name: 'Worldwide',
            },
          ],
          serviceType: [
            'Portrait Photography',
            'Wedding Photography',
            'Boudoir Photography',
            'Brand Photography',
            'Nature Photography',
            'Fine Art Prints',
          ],
          sameAs: [
            'https://www.instagram.com/raven__moon__photography',
            'https://www.facebook.com/share/188TB5SuQ6/',
            'https://www.tiktok.com/@raven_moon_photography',
          ],
          openingHoursSpecification: {
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
            opens: '09:00',
            closes: '18:00',
          },
          founder: {
            '@type': 'Person',
            name: 'Tiffany Laws',
            jobTitle: 'Photographer & Owner',
          },
        };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute('data-seo', 'structured-data');
    script.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script);

    // Add additional structured data (e.g., FAQPage, BreadcrumbList)
    if (additionalStructuredData) {
      const existingAdditional = document.querySelector('script[data-seo="additional-structured-data"]');
      if (existingAdditional) existingAdditional.remove();
      
      const additionalScript = document.createElement('script');
      additionalScript.type = 'application/ld+json';
      additionalScript.setAttribute('data-seo', 'additional-structured-data');
      additionalScript.textContent = JSON.stringify(additionalStructuredData);
      document.head.appendChild(additionalScript);
    }

    return () => {
      const scriptToRemove = document.querySelector('script[data-seo="structured-data"]');
      if (scriptToRemove) scriptToRemove.remove();
      const additionalToRemove = document.querySelector('script[data-seo="additional-structured-data"]');
      if (additionalToRemove) additionalToRemove.remove();
    };
  }, [fullTitle, description, keywords, fullImage, fullUrl, type, publishedTime, author, additionalStructuredData]);

  return null;
});

SEO.displayName = 'SEO';
